/**
 * Report shell screen primitives — locked tokens, mobile-first.
 */
import { useNavigate } from 'react-router-dom'
import { formatCurrency } from '../../../lib/formatters'
import type { OperationsReportPayload } from '../../../api/operationsReport'
import type { DrillDownView } from '../../dashboard/DashboardDrillDownPanel'
import { pickStoryLeadClient, pickTopPendingPayAppId } from './reportStoryLead'
import './ReportShellSections.css'

function trendLabel(pct: number | null | undefined): string {
  if (pct == null) return ''
  return `${pct > 0 ? '+' : ''}${pct}% vs prior`
}

const KPI_DRILL: Partial<Record<string, DrillDownView>> = {
  Billed: 'billed',
  Collected: 'collected',
  Outstanding: 'outstanding',
}

export function OpsKpiSection({
  data,
  onDrillDown,
}: {
  data: OperationsReportPayload
  onDrillDown?: (view: DrillDownView) => void
}) {
  const m = data.metrics
  const items = [
    ['Billed', m.billed, false],
    ['Collected', m.collected, false],
    ['Outstanding', m.outstanding, false],
    ['Retainage held', m.retainageHeld, false],
    ['Avg days to paid', m.avgDaysToPaid, true],
  ] as const

  return (
    <section className="rpt-section" data-testid="ops-section-kpis">
      <h2 className="rpt-section-title">Key metrics</h2>
      <div className="rpt-kpi-grid">
        {items.map(([label, metric, isDays]) => {
          const drillView = KPI_DRILL[label]
          const clickable = drillView && onDrillDown
          return (
            <div
              key={label}
              className={`rpt-kpi-card${clickable ? ' rpt-kpi-clickable' : ''}`}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              data-testid={drillView ? `ops-kpi-${drillView}` : undefined}
              onClick={clickable ? () => onDrillDown(drillView) : undefined}
              onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onDrillDown(drillView) } : undefined}
            >
              <div className="rpt-kpi-label">{label}</div>
              <div className="rpt-kpi-value">
                {isDays ? (metric.value != null ? `${metric.value}d` : '—') : formatCurrency(metric.value ?? 0)}
              </div>
              {label === 'Billed' && (
                <div className="rpt-kpi-sub" data-testid="ops-billed-of-which-collected">
                  of which collected {formatCurrency(m.collected?.value ?? 0)}
                </div>
              )}
              {metric.trendPct != null && (
                <div className={`rpt-kpi-delta ${metric.trendPct < 0 ? 'neg' : ''}`}>{trendLabel(metric.trendPct)}</div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

function SvgBarChart({ title, bars, testId }: {
  title: string
  testId: string
  bars: Array<{ label: string; value: number; sub?: string }>
}) {
  const max = Math.max(...bars.map(b => b.value), 1)
  const hasData = bars.some(b => b.value > 0)
  if (!hasData) {
    return (
      <section className="rpt-section" data-testid={testId}>
        <h2 className="rpt-section-title">{title}</h2>
        <p className="rpt-empty">No data for this period.</p>
      </section>
    )
  }
  return (
    <section className="rpt-section" data-testid={testId}>
      <h2 className="rpt-section-title">{title}</h2>
      <div className="rpt-bar-chart" role="img" aria-label={title}>
        {bars.map(b => (
          <div key={b.label} className="rpt-bar-col">
            <div className="rpt-bar-track">
              <div className="rpt-bar-fill" style={{ height: `${Math.max(4, (b.value / max) * 100)}%` }}>
                <span className="rpt-bar-val">{formatCurrency(b.value)}</span>
              </div>
            </div>
            <div className="rpt-bar-label">{b.label}</div>
            {b.sub && <div className="rpt-bar-sub">{b.sub}</div>}
          </div>
        ))}
      </div>
    </section>
  )
}

export function OpsAgingChartSection({ data }: { data: OperationsReportPayload }) {
  const ag = data.aging
  return SvgBarChart({
    title: 'Receivables aging',
    testId: 'ops-section-aging-chart',
    bars: [
      { label: '0–30', value: ag.bucket0_30?.amount ?? 0, sub: `${ag.bucket0_30?.count ?? 0} items` },
      { label: '31–60', value: ag.bucket31_60?.amount ?? 0, sub: `${ag.bucket31_60?.count ?? 0} items` },
      { label: '60+', value: ag.bucket60plus?.amount ?? 0, sub: `${ag.bucket60plus?.count ?? 0} items` },
    ],
  })
}

export function OpsMonthlyChartSection({ data }: { data: OperationsReportPayload }) {
  const rows = data.monthlyTrend ?? []
  const max = Math.max(...rows.flatMap(r => [r.billed, r.collected]), 1)
  if (!rows.length || max <= 0) {
    return (
      <section className="rpt-section" data-testid="ops-section-monthly-chart">
        <h2 className="rpt-section-title">Billed vs collected, last 6 months</h2>
        <p className="rpt-empty">No billing activity in the last six months.</p>
      </section>
    )
  }
  return (
    <section className="rpt-section" data-testid="ops-section-monthly-chart">
      <h2 className="rpt-section-title">Billed vs collected, last 6 months</h2>
      <div className="rpt-paired-chart">
        {rows.map(row => (
          <div key={row.label} className="rpt-paired-col">
            <div className="rpt-paired-bars">
              <div className="rpt-bar-fill brick" style={{ height: `${Math.max(4, (row.billed / max) * 100)}%` }} title={`Billed ${formatCurrency(row.billed)}`} />
              <div className="rpt-bar-fill brass" style={{ height: `${Math.max(4, (row.collected / max) * 100)}%` }} title={`Collected ${formatCurrency(row.collected)}`} />
            </div>
            <div className="rpt-bar-label">{row.label}</div>
          </div>
        ))}
      </div>
      <div className="rpt-legend">
        <span><i className="swatch brick" /> Billed</span>
        <span><i className="swatch brass" /> Collected</span>
      </div>
    </section>
  )
}

export function OpsOpenPayAppsSection({ data }: { data: OperationsReportPayload }) {
  const nav = useNavigate()
  const lienByProject: Record<number, { days_remaining?: number; alert_type?: string }> = {}
  for (const lw of data.completeness?.openLienWindows ?? []) {
    const pid = lw.project_id as number
    if (pid != null) lienByProject[pid] = lw as { days_remaining?: number; alert_type?: string }
  }
  const rows = (data.openPayApps ?? []).slice().sort(
    (a, b) => (b.days_outstanding ?? 0) - (a.days_outstanding ?? 0),
  )

  function openPayApp(r: NonNullable<OperationsReportPayload['openPayApps']>[number]) {
    if (r.id != null && r.project_id != null) {
      nav(`/projects/${r.project_id}/pay-apps/${r.id}?step=5`)
    }
  }

  return (
    <section className="rpt-section" data-testid="ops-section-open-payapps">
      <h2 className="rpt-section-title">Open pay apps</h2>
      {!rows.length ? (
        <p className="rpt-empty">No open pay apps — nice work.</p>
      ) : (
        <div className="rpt-table-wrap">
          <table className="rpt-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>PA#</th>
                <th className="num">Amount</th>
                <th className="num">Days out</th>
                <th>Lien window</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const lw = lienByProject[r.project_id as number]
                let lien = '—'
                if (lw) {
                  const d = lw.days_remaining
                  lien = d != null ? (d >= 0 ? `${d}d left` : 'past due') : String(lw.alert_type || 'open')
                }
                return (
                  <tr
                    key={`${r.project_id}-${r.app_number}-${i}`}
                    className={`rpt-drill-row${i % 2 ? ' zebra' : ''}`}
                    data-testid="ops-open-payapp-row"
                    onClick={() => openPayApp(r)}
                  >
                    <td>
                      <button
                        type="button"
                        className="rpt-drill-link"
                        data-testid="ops-project-link"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (r.project_id != null) nav(`/projects?pm=${r.project_id}&tab=payments`)
                        }}
                      >
                        {String(r.project_name ?? '—')}
                      </button>
                    </td>
                    <td>{r.app_number != null ? `#${r.app_number}` : '—'}</td>
                    <td className="num mono">{formatCurrency(Number(r.balance ?? 0))}</td>
                    <td className="num mono">{r.days_outstanding ?? '—'}</td>
                    <td>{lien}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export function OpsCalloutSection({ data }: { data: OperationsReportPayload }) {
  const nav = useNavigate()
  const text = pickStoryLeadClient(data)
  const topPaId = pickTopPendingPayAppId(data)

  return (
    <section className="rpt-section" data-testid="ops-section-callout">
      {topPaId != null ? (
        <button
          type="button"
          className="rpt-callout rpt-callout-link"
          data-testid="ops-callout-link"
          onClick={() => nav(`/aria/follow-ups?pa=${topPaId}`)}
        >
          {text}
        </button>
      ) : (
        <div className="rpt-callout" data-testid="ops-callout-static">{text}</div>
      )}
    </section>
  )
}
